/**
 * Turning the paralegal's ticks into the share the backend stores.
 *
 * This is separate from the modal because it is the part that decides
 * what leaves the firm. Ant Design's checkable tree and our grant model
 * disagree about what a tick means, and reconciling them is the whole job
 * here:
 *
 *   - antd, with `checkStrictly` off, reports every fully-checked node —
 *     so ticking one parent comes back as the parent *and* all six
 *     folders beneath it.
 *   - a grant stores the tick as a **standing instruction**: the folder,
 *     never its current contents, so a subfolder made next week is
 *     already covered.
 *
 * Sending antd's list verbatim would work today and drift tomorrow. It
 * would also make the share read back as six folder names when the
 * paralegal ticked one, which is the sort of small dishonesty that stops
 * people trusting a screen. So ticks are pruned to their top-most
 * ancestor before they are sent.
 */

/** Flat index of the API's nested folders: id → { folder, parentId }. */
export const indexFolders = (folders = [], parentId = null, into = {}) => {
  folders.forEach((folder) => {
    into[folder.id] = { folder, parentId };
    indexFolders(folder.subfolders || [], folder.id, into);
  });
  return into;
};

/** antd `treeData`, with work-product folders present but untickable. */
export const toTreeNodes = (folders = []) =>
  folders.map((folder) => ({
    key: folder.id,
    // The reason travels with the node so the row can explain itself.
    // A greyed-out checkbox with no explanation reads as a broken screen.
    title: folder.name,
    documentCount: folder.document_count,
    sendableCount: folder.sendable_count,
    shareable: folder.shareable,
    reason: folder.reason,
    disableCheckbox: !folder.shareable,
    children: toTreeNodes(folder.subfolders || []),
  }));

/**
 * Keep only ticks with no ticked ancestor.
 *
 * The result is what gets stored, and it is deliberately the smallest set
 * that means the same thing: one row saying "Claim File" rather than
 * seven rows that happen to enumerate it today.
 */
export const topmostChecked = (checkedKeys = [], index = {}) => {
  const checked = new Set(checkedKeys.map(String));
  return checkedKeys.map(String).filter((key) => {
    let { parentId } = index[key] || {};
    while (parentId != null) {
      if (checked.has(String(parentId))) return false;
      ({ parentId } = index[parentId] || {});
    }
    return true;
  });
};

/**
 * What the send actually amounts to.
 *
 * `sending` is the number the paralegal is deciding about. `heldBack` is
 * the gap between the folders as they know them and what a tick can
 * deliver — work product filed under something they ticked. Reporting the
 * gap rather than silently subtracting it is the difference between a
 * screen that is right and a screen that is trusted: a paralegal who
 * ticks a folder of 16 and is told 14 needs to know where the other two
 * went, or they will assume the portal is broken.
 */
export const summariseSend = ({
  checkedKeys = [],
  index = {},
  includeUnfiled = false,
  unfiledCount = 0,
}) => {
  const roots = topmostChecked(checkedKeys, index);
  let sending = includeUnfiled ? unfiledCount : 0;
  let heldBack = 0;

  // Pruned ticks are disjoint subtrees, so these sums cannot double-count.
  roots.forEach((key) => {
    const { folder } = index[key] || {};
    if (!folder) return;
    sending += folder.sendable_count || 0;
    heldBack += (folder.document_count || 0) - (folder.sendable_count || 0);
  });

  return { folderIds: roots, sending, heldBack };
};

/** The folder ids already on a share, for re-opening the picker on it. */
export const checkedKeysForShare = (share) =>
  (share?.folders || []).map((folder) => String(folder.id));
